/*
 * Settings → Operations → Earthworks → Numbering (api 0617).
 *
 * A prefix and next number for each earthworks series — jobs, field tickets,
 * change orders (GET / PUT /earthworks/settings/numbering). Admin and office
 * staff see it; only an admin may change it. Quotes and invoices use the
 * company's shared series and are not here.
 *
 * The next number cannot be set at or below a number already issued under
 * that prefix: the API answers 409 counter_below_issued and says the lowest
 * number allowed. That message is shown as it comes. Only changed fields are
 * sent, under the snake_case series keys; a cleared prefix is sent as null,
 * which puts the series back on its default.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useEwNumbering, useUpdateEwNumbering } from '../../hooks/useEarthworks';
import { ewApiError, ewRefusal } from '../../lib/earthworks';
import type { EwNumbering, EwNumberingSeriesKey, EwNumberingUpdate } from '../../types/earthworks';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';
const btnPrimary = 'flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed';

/** Same rule as the API (EW_PREFIX_RE): letters, digits, hyphens; starts with a letter or digit; 1–10 long. */
const PREFIX_RE = /^[A-Za-z0-9][A-Za-z0-9-]{0,9}$/;
const MAX_NUMBER = 999_999_999;

const ROWS: { key: EwNumberingSeriesKey; read: keyof EwNumbering; label: string }[] = [
  { key: 'job', read: 'job', label: 'Jobs' },
  { key: 'ticket', read: 'ticket', label: 'Field tickets' },
  { key: 'change_order', read: 'changeOrder', label: 'Change orders' },
];

type Draft = Record<EwNumberingSeriesKey, { prefix: string; next: string }>;

const toDraft = (n: EwNumbering): Draft => ({
  job: { prefix: n.job.prefix, next: String(n.job.nextNumber) },
  ticket: { prefix: n.ticket.prefix, next: String(n.ticket.nextNumber) },
  change_order: { prefix: n.changeOrder.prefix, next: String(n.changeOrder.nextNumber) },
});

/** `EWJ-0110` — the same 4-digit padding the API uses. */
const preview = (prefix: string, next: string) => {
  const n = Number(next);
  return Number.isInteger(n) && n > 0 ? `${prefix}${String(n).padStart(4, '0')}` : '—';
};

export default function EwNumberingSection({ role }: { role: string | undefined }) {
  const canSee = role === 'admin' || role === 'office_staff';
  const canWrite = role === 'admin';
  const query = useEwNumbering(canSee);
  const save = useUpdateEwNumbering();
  const numbering = query.data?.data ?? null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (numbering) setDraft(toDraft(numbering));
  }, [numbering]);

  if (!canSee) return null;

  return (
    <section className="space-y-3 border-t border-fieldiq-border pt-4" data-testid="ew-numbering">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Numbering</h2>
          <p className="text-xs text-fieldiq-muted">
            The prefix and next number for new jobs, field tickets and change orders. Quotes and invoices use the company series.
          </p>
        </div>
        {canWrite && numbering && draft && (
          <SaveButton numbering={numbering} draft={draft} pending={save.isPending} onSave={(body) => {
            setError(null);
            save.mutate(body, {
              onSuccess: () => toast.success('Numbering saved'),
              onError: (err) =>
                setError(ewApiError(err).status === 403
                  ? 'Only an admin can change numbering.'
                  : ewRefusal(err, 'The numbering could not be saved.')),
            });
          }} onInvalid={setError} />
        )}
      </div>
      {!canWrite && (
        <p className="text-xs text-fieldiq-muted" data-testid="ew-numbering-readonly">Only an admin can change numbering.</p>
      )}
      {error && (
        <p role="alert" className="rounded-lg border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
          {error}
        </p>
      )}

      {query.isLoading && <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading numbering" />}
      {query.isError && (
        <p role="alert" className="text-sm text-fieldiq-text">
          {ewRefusal(query.error, 'Numbering could not be loaded.')}
        </p>
      )}

      {numbering && draft && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-fieldiq-muted">
                <th className="py-2 pr-3 font-medium">Series</th>
                <th className="py-2 pr-3 font-medium">Prefix</th>
                <th className="py-2 pr-3 font-medium">Next number</th>
                <th className="py-2 pr-3 font-medium">Next will be</th>
                <th className="py-2 font-medium">Highest issued</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => {
                const cur = numbering[r.read];
                const d = draft[r.key];
                return (
                  <tr key={r.key} className="border-t border-fieldiq-border" data-testid={`ew-numbering-${r.key}`}>
                    <td className="py-2 pr-3 text-white">{r.label}</td>
                    <td className="py-2 pr-3 min-w-[8rem]">
                      <input aria-label={`${r.label} prefix`} className={inputCls} value={d.prefix} maxLength={10} disabled={!canWrite}
                        onChange={(e) => setDraft({ ...draft, [r.key]: { ...d, prefix: e.target.value } })} />
                    </td>
                    <td className="py-2 pr-3 min-w-[8rem]">
                      <input aria-label={`${r.label} next number`} className={inputCls} inputMode="numeric" value={d.next} disabled={!canWrite}
                        onChange={(e) => setDraft({ ...draft, [r.key]: { ...d, next: e.target.value } })} />
                    </td>
                    <td className="py-2 pr-3 text-fieldiq-text whitespace-nowrap">{preview(d.prefix.trim(), d.next.trim())}</td>
                    <td className="py-2 text-fieldiq-text">{cur.highestIssued > 0 ? cur.highestIssued : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-xs text-fieldiq-muted mt-1">
            The next number must be higher than the highest number already issued under that prefix. Clear a prefix to go back to the default.
          </p>
        </div>
      )}
    </section>
  );
}

function SaveButton({ numbering, draft, pending, onSave, onInvalid }: {
  numbering: EwNumbering;
  draft: Draft;
  pending: boolean;
  onSave: (body: EwNumberingUpdate) => void;
  onInvalid: (msg: string) => void;
}) {
  const body: EwNumberingUpdate = {};
  for (const r of ROWS) {
    const cur = numbering[r.read];
    const d = draft[r.key];
    const change: { prefix?: string | null; next_number?: number } = {};
    // A cleared prefix is null: the series goes back to its default (EWJ- / EWT- / EWC-).
    if (d.prefix.trim() !== cur.prefix) change.prefix = d.prefix.trim() === '' ? null : d.prefix.trim();
    if (d.next.trim() !== String(cur.nextNumber)) change.next_number = Number(d.next.trim());
    if (Object.keys(change).length) body[r.key] = change;
  }
  const dirty = Object.keys(body).length > 0;

  const submit = () => {
    for (const r of ROWS) {
      const c = body[r.key];
      if (!c) continue;
      if (typeof c.prefix === 'string' && !PREFIX_RE.test(c.prefix)) {
        return onInvalid(`${r.label}: the prefix must be 1 to 10 letters, digits or hyphens, starting with a letter or digit, e.g. EWJ-.`);
      }
      if (c.next_number !== undefined && (!Number.isInteger(c.next_number) || c.next_number < 1 || c.next_number > MAX_NUMBER)) {
        return onInvalid(`${r.label}: the next number must be a whole number from 1 to ${MAX_NUMBER.toLocaleString('en-CA')}.`);
      }
    }
    onSave(body);
  };

  return (
    <button type="button" onClick={submit} disabled={!dirty || pending} className={btnPrimary} data-testid="ew-numbering-save">
      {pending ? <Loader2 size={16} className="animate-spin" /> : null}
      {pending ? 'Saving...' : 'Save numbering'}
    </button>
  );
}
